import React from 'react';
import { FileText, ShoppingBag, Video, MessageSquare, Clock, Download, Sparkles, BookOpen, ChevronRight, User as UserIcon, Package } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useToast } from '../ui/Toast';
import { useLanguage } from '../../i18n/LanguageContext';
import { ZodiacTheme } from './zodiacThemes';
import { User } from '../../types';
import { usePackages } from '../../contexts/PackagesContext';
interface OverviewTabProps {
  theme: ZodiacTheme;
  user: User | null;
  setActiveTab: (tab: string) => void;
}
const kpiItems = [{
  id: 'orders',
  label: 'Заказы',
  value: 2,
  Icon: ShoppingBag
}, {
  id: 'reports',
  label: 'Отчёты',
  value: 4,
  Icon: FileText
}, {
  id: 'consultations',
  label: 'Сессии',
  value: 1,
  Icon: Video
}, {
  id: 'chat',
  label: 'Сообщения',
  value: 3,
  Icon: MessageSquare,
  alert: true
}] as const;
const reports = [{
  title: 'Натальная карта',
  date: '12 Окт 2024',
  pending: false
}, {
  title: 'Соляр 2024',
  date: '05 Окт 2024',
  pending: false
}, {
  title: 'Совместимость',
  date: 'В процессе',
  pending: true
}];
const messages = [{
  name: 'Елена Смирнова',
  msg: 'Здравствуйте! Я посмотрела вашу карту...',
  time: '10:45',
  unread: 2
}, {
  name: 'Служба поддержки',
  msg: 'Ваш заказ успешно оплачен и передан...',
  time: 'Вчера',
  unread: 0
}, {
  name: 'Дмитрий Волков',
  msg: 'Спасибо за консультацию!',
  time: '12 Окт',
  unread: 0
}];
const purchases = [{
  title: 'Основы астрологии',
  type: 'Видеокурс',
  Icon: Video
}, {
  title: 'Лунные узлы и карма',
  type: 'PDF Гайд',
  Icon: BookOpen
}];
export function OverviewTab({
  theme,
  user,
  setActiveTab
}: OverviewTabProps) {
  const {
    showToast
  } = useToast();
  const {
    t
  } = useLanguage();
  const {
    activePackages,
    totalRemaining,
    totalUsed
  } = usePackages();
  const hasActivePackage = activePackages.length > 0;
  const oldestPackage = activePackages.length > 0 ? activePackages[0] : null;
  const kpiItems = [{
    id: 'orders',
    label: t('userdash.overview.kpiOrders'),
    value: 2,
    Icon: ShoppingBag
  }, {
    id: 'reports',
    label: t('userdash.overview.kpiReports'),
    value: 4,
    Icon: FileText
  }, {
    id: 'consultations',
    label: t('userdash.overview.kpiSessions'),
    value: 1,
    Icon: Video
  }, {
    id: 'chat',
    label: t('userdash.overview.kpiMessages'),
    value: 3,
    Icon: MessageSquare,
    alert: true
  }] as const;
  const reports = [{
    title: t('userdash.overview.natalChart'),
    date: '12 Окт 2024',
    pending: false
  }, {
    title: t('userdash.overview.solar'),
    date: '05 Окт 2024',
    pending: false
  }, {
    title: t('userdash.overview.compatibility'),
    date: t('userdash.overview.inProcessDate'),
    pending: true
  }];
  const messages = [{
    name: 'Елена Смирнова',
    msg: 'Здравствуйте! Я посмотрела вашу карту...',
    time: '10:45',
    unread: 2
  }, {
    name: 'Служба поддержки',
    msg: 'Ваш заказ успешно оплачен и передан...',
    time: 'Вчера',
    unread: 0
  }, {
    name: 'Дмитрий Волков',
    msg: 'Спасибо за консультацию!',
    time: '12 Окт',
    unread: 0
  }];
  const purchases = [{
    title: t('userdash.overview.basicsAstrology'),
    type: t('userdash.overview.videoCourse'),
    Icon: Video
  }, {
    title: t('userdash.overview.lunarNodes'),
    type: t('userdash.overview.pdfGuide'),
    Icon: BookOpen
  }];
  const SectionHeader = ({
    title,
    tab,
    linkText = 'Все'
  }: {
    title: string;
    tab: string;
    linkText?: string;
  }) => <div className="flex justify-between items-center" data-id="element-19848">
      <h3 className="font-bold text-slate-900" data-id="element-19849">{title}</h3>
      <button onClick={() => setActiveTab(tab)} className={`text-sm font-medium ${theme.text} hover:underline flex items-center gap-0.5`} data-id="element-19850">
        {linkText} <ChevronRight className="w-4 h-4" data-id="element-19851" />
      </button>
    </div>;
  return <div className="space-y-6 pb-8" data-id="element-19852">
      {/* ── 1. Баннер ── */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 shadow-sm relative overflow-hidden" data-id="element-19853">
        <div className="absolute top-0 right-0 -mt-6 -mr-6 text-[120px] opacity-5 pointer-events-none leading-none select-none" data-id="element-19854">
          {theme.icon}
        </div>
        <div className="relative z-10" data-id="element-19855">
          <span className={`inline-block bg-slate-100 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider mb-3 ${theme.text}`} data-id="element-19856">
            {theme.name} • {theme.element}
          </span>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1.5" data-id="element-19857">
            {t('userdash.overview.welcome', {
            name: user?.name?.split(' ')[0] || t('userdash.overview.guest')
          })}
          </h2>
          <p className="text-slate-500 text-sm mb-4 max-w-lg leading-relaxed" data-id="element-19858">
            {t('userdash.overview.todayMoon')}
          </p>
          <div className="flex gap-2.5" data-id="element-19859">
            <Button size="sm" onClick={() => showToast(t('userdash.overview.goToServices'))} data-id="element-19860">
              {t('userdash.overview.orderService')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setActiveTab('reports')} data-id="element-19861">
              {t('userdash.overview.myReports')}
            </Button>
          </div>
        </div>
      </div>

      {/* ── 2. KPI — единая полоса ── */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-id="element-19862">
        <div className="flex overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]" data-id="element-19863">
          {kpiItems.map((kpi, idx) => <button key={kpi.id} onClick={() => setActiveTab(kpi.id)} className={`flex-1 min-w-[120px] flex items-center gap-2.5 px-4 py-3 hover:bg-slate-50 transition-colors relative ${idx > 0 ? 'border-l border-slate-100' : ''}`} data-id="element-19864">
              {kpi.alert && <span className="absolute top-2 right-2 w-2 h-2 bg-rose-500 rounded-full" data-id="element-19865" />}
              <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-500" data-id="element-19866">
                <kpi.Icon className="w-4 h-4" data-id="element-19867" />
              </div>
              <div className="text-left" data-id="element-19868">
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-19869">
                  {kpi.label}
                </div>
                <div className="text-sm font-bold text-slate-900" data-id="element-19870">
                  {kpi.value}
                </div>
              </div>
            </button>)}
        </div>
      </div>

      {/* ── 2.5 Пакеты отчетов ── */}
      {hasActivePackage && oldestPackage && <Card className="p-5 border-indigo-100 bg-gradient-to-r from-indigo-50/50 to-purple-50/50" data-id="element-19871">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-19872">
            <div className="flex items-center gap-4" data-id="element-19873">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${theme.gradient} flex items-center justify-center text-white shadow-sm flex-shrink-0`} data-id="element-19874">
                <Package className="w-6 h-6" data-id="element-19875" />
              </div>
              <div data-id="element-19876">
                <h3 className="text-sm font-bold text-slate-900 mb-1" data-id="element-19877">
                  Ваш пакет отчетов
                </h3>
                <div className="flex items-center gap-3 text-xs" data-id="element-19878">
                  <span className="text-emerald-600 font-medium" data-id="element-19879">
                    Осталось: {totalRemaining}
                  </span>
                  <span className="text-slate-400" data-id="element-19880">•</span>
                  <span className="text-slate-500" data-id="element-19881">
                    Использовано: {totalUsed}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex gap-2" data-id="element-19882">
              <Button size="sm" onClick={() => setActiveTab('packages')} data-id="element-19883">
                Все пакеты
              </Button>
            </div>
          </div>
        </Card>}

      {/* ── 3. Основная сетка (Отчёты / Консультации / Чат) ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5" data-id="element-19884">
        {/* ── 3.1. Отчёты ── */}
        <section className="space-y-2.5 min-w-0" data-id="element-19885">
          <SectionHeader title={t('userdash.overview.latestReports')} tab="reports" data-id="element-19886" />
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm divide-y divide-slate-100" data-id="element-19887">
            {reports.map((r, i) => <div key={i} className="flex items-center justify-between px-3 py-2.5 hover:bg-slate-50 transition-colors group cursor-pointer" onClick={() => setActiveTab('reports')} data-id="element-19888">
                <div className="flex items-center gap-2.5 min-w-0 flex-1" data-id="element-19889">
                  <div className={`w-8 h-8 rounded-lg ${r.pending ? 'bg-slate-100 text-slate-400' : `${theme.bg} ${theme.text}`} flex items-center justify-center shrink-0`} data-id="element-19890">
                    <FileText className="w-3.5 h-3.5" data-id="element-19891" />
                  </div>
                  <div className="min-w-0 flex-1" data-id="element-19892">
                    <div className="font-semibold text-sm text-slate-900 truncate" data-id="element-19893">
                      {r.title}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1.5" data-id="element-19894">
                      {r.date}
                      {r.pending && <Badge variant="warning" size="sm" className="text-[9px] py-0 px-1 h-3.5 leading-none" data-id="element-19895">
                          {t('userdash.overview.inProgress')}
                        </Badge>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0 ml-2" data-id="element-19896">
                  {!r.pending && <button className="p-1 text-slate-400 hover:text-slate-700 transition-colors hidden lg:block" title={t('userdash.overview.downloadPdf')} onClick={e => {
                e.stopPropagation();
                showToast(t('userdash.overview.downloading'));
              }} data-id="element-19897">
                      <Download className="w-3.5 h-3.5" data-id="element-19898" />
                    </button>}
                  <Button variant={r.pending ? 'outline' : 'secondary'} size="sm" className="text-[11px] px-2 h-6" onClick={e => {
                e.stopPropagation();
                setActiveTab('reports');
              }} data-id="element-19899">
                    {t('userdash.overview.open')}
                  </Button>
                </div>
              </div>)}
          </div>
        </section>

        {/* ── 3.2. Консультации ── */}
        <section className="space-y-2.5 min-w-0" data-id="element-19900">
          <SectionHeader title={t('userdash.overview.nextSession')} tab="consultations" data-id="element-19901" />
          <div className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-sm relative overflow-hidden" data-id="element-19902">
            <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500 rounded-l-xl" data-id="element-19903" />
            <div className="pl-2.5" data-id="element-19904">
              <div className="flex items-center gap-2 mb-2 flex-wrap" data-id="element-19905">
                <Badge variant="pro" size="sm" className="bg-indigo-50 text-indigo-700 text-[10px]" data-id="element-19906">
                  {t('userdash.overview.awaiting')}
                </Badge>
                <span className="text-[11px] text-slate-500 flex items-center gap-1" data-id="element-19907">
                  <Video className="w-3 h-3" data-id="element-19908" />{' '}
                  {t('userdash.overview.videoCall')}
                </span>
              </div>
              <h4 className="font-bold text-sm text-slate-900 mb-1.5 truncate" data-id="element-19909">
                {t('userdash.overview.synastry')}
              </h4>
              <div className="flex items-center gap-2 text-xs text-slate-600 mb-2.5" data-id="element-19910">
                <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-[8px] shrink-0" data-id="element-19911">
                  ЕС
                </div>
                <span className="truncate" data-id="element-19912">Елена Смирнова</span>
              </div>
              <div className="inline-flex items-center bg-amber-50 text-amber-800 text-[11px] font-medium px-2 py-0.5 rounded-md border border-amber-100 mb-3" data-id="element-19913">
                <Clock className="w-3 h-3 mr-1 text-amber-500" data-id="element-19914" />
                {t('userdash.overview.tomorrow')}
              </div>
              <div className="flex gap-2" data-id="element-19915">
                <Button variant="outline" size="sm" className="flex-1 text-[11px] h-7" onClick={() => setActiveTab('chat')} data-id="element-19916">
                  <MessageSquare className="w-3.5 h-3.5 mr-1" data-id="element-19917" />{' '}
                  {t('userdash.overview.chatBtn')}
                </Button>
                <Button variant="primary" size="sm" className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-[11px] h-7" onClick={() => showToast(t('userdash.overview.waitForLink'))} data-id="element-19918">
                  {t('userdash.overview.joinCall')}
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* ── 3.3. Сообщения ── */}
        <section className="space-y-2.5 min-w-0 md:col-span-2 lg:col-span-1" data-id="element-19919">
          <SectionHeader title={t('userdash.overview.messages')} tab="chat" data-id="element-19920" />
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm divide-y divide-slate-100" data-id="element-19921">
            {messages.map((m, i) => <div key={i} className={`flex items-center gap-2.5 px-3 py-2.5 hover:bg-slate-50 transition-colors cursor-pointer ${m.unread > 0 ? 'bg-indigo-50/30' : ''}`} onClick={() => setActiveTab('chat')} data-id="element-19922">
                <div className="relative shrink-0" data-id="element-19923">
                  <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-[10px]" data-id="element-19924">
                    {m.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                  </div>
                  {m.unread > 0 && <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-rose-500 rounded-full border-2 border-white flex items-center justify-center text-[8px] font-bold text-white" data-id="element-19925">
                      {m.unread}
                    </span>}
                </div>
                <div className="flex-1 min-w-0" data-id="element-19926">
                  <div className="flex justify-between items-baseline gap-2" data-id="element-19927">
                    <span className={`text-sm truncate ${m.unread > 0 ? 'font-bold text-slate-900' : 'font-medium text-slate-700'}`} data-id="element-19928">
                      {m.name}
                    </span>
                    <span className={`text-[10px] shrink-0 ${m.unread > 0 ? 'text-indigo-600 font-bold' : 'text-slate-400'}`} data-id="element-19929">
                      {m.time}
                    </span>
                  </div>
                  <div className={`text-xs truncate mt-0.5 ${m.unread > 0 ? 'text-slate-800' : 'text-slate-500'}`} data-id="element-19930">
                    {m.msg}
                  </div>
                </div>
                <ChevronRight className="w-3.5 h-3.5 text-slate-300 shrink-0 hidden sm:block" data-id="element-19931" />
              </div>)}
          </div>
        </section>
      </div>

      {/* ── 4. Астропрофиль ── */}
      <section className="space-y-2.5" data-id="element-19932">
        <SectionHeader title={t('userdash.overview.myAstroProfile')} tab="profile" linkText={t('userdash.overview.allProfiles')} data-id="element-19933" />
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-id="element-19934">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-19935">
            <div className="flex items-center gap-3" data-id="element-19936">
              <div className={`w-11 h-11 rounded-full bg-gradient-to-br ${theme.gradient} flex items-center justify-center text-white text-lg shadow-sm shrink-0`} data-id="element-19937">
                {theme.icon}
              </div>
              <div data-id="element-19938">
                <div className="flex items-center gap-1.5" data-id="element-19939">
                  <span className="font-bold text-slate-900 text-sm" data-id="element-19940">
                    {user?.name || 'Мой профиль'}
                  </span>
                  <Badge variant="neutral" size="sm" className="text-[9px] py-0 h-3.5 px-1" data-id="element-19941">
                    {t('userdash.overview.primary')}
                  </Badge>
                </div>
                <div className="text-xs text-slate-500 mt-0.5" data-id="element-19942">
                  {theme.name} • 12 Апр 1990, 14:30 • Москва
                </div>
              </div>
            </div>
            <div className="flex gap-2 w-full sm:w-auto shrink-0" data-id="element-19943">
              <Button variant="secondary" size="sm" className="flex-1 sm:flex-none text-xs" onClick={() => showToast(t('userdash.overview.profileSelected'))} data-id="element-19944">
                {t('userdash.overview.use')}
              </Button>
              <Button variant="outline" size="sm" className="px-2.5" onClick={() => setActiveTab('profile')} title="Редактировать" data-id="element-19945">
                <UserIcon className="w-4 h-4" data-id="element-19946" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ── 5. Покупки ── */}
      <section className="space-y-2.5" data-id="element-19947">
        <SectionHeader title={t('userdash.overview.recentPurchases')} tab="purchases" data-id="element-19948" />
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm divide-y divide-slate-100" data-id="element-19949">
          {purchases.map((p, i) => <div key={i} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors" data-id="element-19950">
              <div className="flex items-center gap-3 min-w-0" data-id="element-19951">
                <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0" data-id="element-19952">
                  <p.Icon className="w-4 h-4" data-id="element-19953" />
                </div>
                <div className="min-w-0" data-id="element-19954">
                  <div className="font-semibold text-sm text-slate-900 truncate" data-id="element-19955">
                    {p.title}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-19956">{p.type}</div>
                </div>
              </div>
              <Button variant="ghost" size="sm" className={`text-xs font-medium ${theme.text} hover:bg-indigo-50 shrink-0 ml-2`} onClick={() => showToast(t('userdash.overview.opening', {
            title: p.title
          }))} data-id="element-19957">
                {t('userdash.overview.open')}
              </Button>
            </div>)}
        </div>
      </section>
    </div>;
}